import { useId, useRef, useState } from 'react';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { Popper } from '@mui/base/Popper';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Box, Button, FormControl, FormLabel, IconButton, Input, Sheet, Typography } from '@mui/joy';
import {
    addDays, addMonths, eachDayOfInterval, format, isSameDay,
    isSameMonth, isValid, parse, startOfDay, startOfMonth, startOfWeek, subDays
} from 'date-fns';
import { ru } from 'date-fns/locale';

const displayDate = day => format(day, 'dd.MM.yyyy');
const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const calendarModifiers = [
    { name: 'offset', options: { offset: [0, 8] } },
    { name: 'flip', options: { boundary: [], rootBoundary: 'viewport', padding: 12 } },
    { name: 'preventOverflow', options: { boundary: [], rootBoundary: 'viewport', padding: 12, altAxis: true, tether: false } }
];

export default function ExpenseDateField({ value, onChange, disabled = false }) {
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [month, setMonth] = useState(startOfMonth(new Date()));
    const toggleRef = useRef(null);
    const inputRef = useRef(null);
    const calendarId = useId();
    const parsed = parse(value, 'dd.MM.yyyy', new Date());
    const selected = isValid(parsed) && displayDate(parsed) === value ? parsed : null;
    const today = startOfDay(new Date());
    const firstDay = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: firstDay, end: addDays(firstDay, 41) });

    const closeCalendar = (restoreFocus = false) => {
        setCalendarOpen(false);
        if (restoreFocus) toggleRef.current?.focus({ preventScroll: true });
    };

    const selectDay = day => {
        onChange(displayDate(day));
        closeCalendar(true);
    };

    return (
        <ClickAwayListener onClickAway={() => closeCalendar()}>
            <FormControl required disabled={disabled} sx={{ minWidth: 0 }}
                onBlur={event => {
                    if (!event.currentTarget.contains(event.relatedTarget)) closeCalendar();
                }}
                onKeyDown={event => {
                    if (calendarOpen && event.key === 'Escape') {
                        event.stopPropagation();
                        closeCalendar(true);
                    }
                }}>
                <FormLabel>Дата операции</FormLabel>
                <Input
                    ref={inputRef}
                    placeholder="ДД.ММ.ГГГГ"
                    value={value}
                    onChange={event => {
                        const text = event.target.value;
                        onChange(/^\d{8}$/.test(text) ? `${text.slice(0, 2)}.${text.slice(2, 4)}.${text.slice(4)}` : text);
                    }}
                    slotProps={{ input: { inputMode: 'numeric', maxLength: 10, autoComplete: 'off' } }}
                    sx={{ height: 44, minHeight: 44, maxHeight: 44 }}
                    endDecorator={
                        <IconButton ref={toggleRef} type="button" disabled={disabled} aria-label="Выбрать дату в календаре"
                            aria-haspopup="dialog" aria-expanded={calendarOpen} aria-controls={calendarOpen ? calendarId : undefined}
                            onClick={() => {
                                if (!calendarOpen) setMonth(startOfMonth(selected || today));
                                setCalendarOpen(open => !open);
                            }}>
                            <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    }
                />
                {/* Остаётся внутри модального окна для управления фокусом,
                    но позиционируется относительно viewport и не растягивает форму. */}
                <Popper open={calendarOpen && !disabled} anchorEl={inputRef.current} placement="bottom-end"
                    disablePortal modifiers={calendarModifiers} popperOptions={{ strategy: 'fixed' }}
                    style={{ zIndex: 1400, width: 'min(304px, calc(100vw - 24px))' }}>
                    <Sheet id={calendarId} role="dialog" aria-label="Выбор даты" variant="outlined"
                        sx={{ p: 1.5, borderRadius: 'lg', boxShadow: 'lg', bgcolor: 'background.surface',
                            maxHeight: 'calc(var(--app-vvh, 100dvh) - 24px)', overflowY: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <IconButton type="button" aria-label="Предыдущий месяц" onClick={() => setMonth(addMonths(month, -1))}>‹</IconButton>
                            <Typography level="title-sm" aria-live="polite" sx={{ textTransform: 'capitalize' }}>
                                {format(month, 'LLLL yyyy', { locale: ru })}
                            </Typography>
                            <IconButton type="button" aria-label="Следующий месяц"
                                disabled={startOfMonth(month) >= startOfMonth(today)}
                                onClick={() => setMonth(addMonths(month, 1))}>›</IconButton>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.25 }}>
                            {weekdays.map(day => <Typography key={day} level="body-xs" textAlign="center" aria-hidden="true">{day}</Typography>)}
                            {days.map(day => (
                                <Button key={format(day, 'yyyy-MM-dd')} type="button" size="sm"
                                    aria-label={displayDate(day)} aria-pressed={!!selected && isSameDay(day, selected)}
                                    aria-current={isSameDay(day, today) ? 'date' : undefined}
                                    disabled={day > today}
                                    variant={selected && isSameDay(day, selected) ? 'solid' : 'plain'}
                                    color={isSameMonth(day, month) ? 'primary' : 'neutral'}
                                    onClick={() => selectDay(day)}
                                    sx={{ minWidth: 0, minHeight: 36, px: 0, fontSize: 13,
                                        ...(isSameDay(day, today) && !(selected && isSameDay(day, selected))
                                            ? { outline: '1px solid', outlineColor: 'primary.200', outlineOffset: '-1px' } : {}) }}>
                                    {format(day, 'd')}
                                </Button>
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider', mt: 1, pt: 1 }}>
                            <Button type="button" variant="plain" size="sm" onClick={() => selectDay(today)}>Сегодня</Button>
                            <Button type="button" variant="plain" size="sm" color="neutral" onClick={() => closeCalendar(true)}>Закрыть</Button>
                        </Box>
                    </Sheet>
                </Popper>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75 }}>
                    <Button type="button" size="sm" variant="plain" disabled={disabled}
                        onClick={() => selectDay(today)} sx={{ p: 0, minHeight: 24 }}>Сегодня</Button>
                    <Button type="button" size="sm" variant="plain" disabled={disabled}
                        onClick={() => selectDay(subDays(today, 1))} sx={{ p: 0, minHeight: 24 }}>Вчера</Button>
                </Box>
            </FormControl>
        </ClickAwayListener>
    );
}
